import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function OperatorProfile({ user, operatorDetails }) {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);

    const [countries, setCountries] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            if (!operatorDetails || !operatorDetails._id) return;
            try {
                const [subRes, countryRes] = await Promise.all([
                    fetch(`${API_URL}/subscriptions/operator/${operatorDetails._id}`),
                    fetch(`${API_URL}/countries`)
                ]);
                
                if (subRes.ok) {
                    const data = await subRes.json();
                    if (data && data.length > 0) {
                        setSubscription(data[0]);
                    }
                }
                
                if (countryRes.ok) {
                    const cData = await countryRes.json();
                    setCountries(cData);
                }
            } catch (err) {
                console.error("Failed to fetch details:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [operatorDetails]);

    if (!operatorDetails) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#6b7280', padding: 40 }}>
                Loading Profile...
            </div>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const InfoRow = ({ label, value }) => (
        <div className="info-row">
            <span className="info-label">{label}</span>
            <span className="info-value">{value || <span style={{color: '#aaa', fontStyle: 'italic'}}>N/A</span>}</span>
        </div>
    );

    let displayCurrency = operatorDetails.defaultCurrency || 'USD';
    let displayAmount = subscription?.amount || 0;

    if (subscription && subscription.planId && operatorDetails.country) {
        const countryObj = countries.find(c => c.name === operatorDetails.country);
        if (countryObj) {
            displayCurrency = countryObj.currency || displayCurrency;
            const specificPriceObj = subscription.planId.prices?.find(p => p.country === operatorDetails.country);
            if (specificPriceObj && specificPriceObj.amount != null) {
                displayAmount = specificPriceObj.amount;
            } else {
                const rate = countryObj.exchangeRate || countryObj.rate || 1;
                displayAmount = (subscription.planId.price || 0) * rate;
            }
        }
    }

    return (
        <div className="profile-wrapper">
            <style>{`
                .profile-wrapper {
                    padding: 30px;
                    max-width: 1400px;
                    margin: 0 auto;
                    font-family: 'Inter', system-ui, -apple-system, sans-serif;
                    animation: fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                    background: transparent;
                    color: #334155;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px) scale(0.98); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                .hero-section {
                    position: relative;
                    border-radius: 24px;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    padding: 40px;
                    color: #0f172a;
                    display: flex;
                    align-items: center;
                    gap: 40px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
                    margin-bottom: 40px;
                    overflow: hidden;
                    border: 1px solid #cbd5e1;
                }
                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: -50%; left: -50%; width: 200%; height: 200%;
                    background: radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 50%);
                    animation: pulseGlow 10s infinite alternate linear;
                    pointer-events: none;
                }
                @keyframes pulseGlow {
                    0% { transform: scale(1) translate(0, 0); }
                    100% { transform: scale(1.2) translate(5%, 5%); }
                }
                .hero-logo-container {
                    position: relative;
                    z-index: 1;
                    width: 140px;
                    height: 140px;
                    border-radius: 50%;
                    background: #ffffff;
                    border: 2px solid rgba(16, 185, 129, 0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 54px;
                    font-weight: 800;
                    color: #10b981;
                    overflow: hidden;
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
                }
                .hero-logo-container img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .hero-content {
                    position: relative;
                    z-index: 1;
                    flex: 1;
                }
                .hero-content h1 {
                    margin: 0 0 16px 0;
                    font-size: 46px;
                    font-weight: 800;
                    letter-spacing: -1px;
                    color: #0f172a;
                    text-shadow: none;
                }
                .hero-badges {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .badge {
                    padding: 8px 18px;
                    border-radius: 30px;
                    font-size: 13px;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                    background: rgba(99, 102, 241, 0.1);
                    border: 1px solid rgba(99, 102, 241, 0.2);
                    color: #16539C;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                }
                .badge:hover {
                    background: rgba(99, 102, 241, 0.15);
                }
                .badge-active {
                    background: rgba(16, 185, 129, 0.1);
                    color: #059669;
                    border-color: rgba(16, 185, 129, 0.3);
                }
                .badge-active:hover {
                    background: rgba(16, 185, 129, 0.15);
                }
                .cards-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
                    gap: 30px;
                }
                .profile-card {
                    background: #f8fafc;
                    border-radius: 20px;
                    padding: 32px;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    position: relative;
                    overflow: hidden;
                    color: #1e293b;
                }
                .profile-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(99, 102, 241, 0.2);
                    border-color: transparent;
                }
                .card-header {
                    margin-bottom: 28px;
                    border-bottom: 1px solid #e2e8f0;
                    padding-bottom: 16px;
                }
                .card-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    letter-spacing: 0.5px;
                }
                .card-title::before {
                    content: '';
                    display: inline-block;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background: #10b981;
                }
                .info-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                .info-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 14px;
                    padding: 12px 16px;
                    border-radius: 12px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    transition: background 0.2s;
                }
                .info-row:hover {
                    background: #f1f5f9;
                    border-color: #cbd5e1;
                }
                .info-label {
                    color: #64748b;
                    font-weight: 600;
                    flex: 1;
                    min-width: 140px;
                }
                .info-value {
                    color: #0f172a;
                    font-weight: 600;
                    flex: 1.5;
                    text-align: right;
                    word-break: break-word;
                }
                .status-pill {
                    padding: 6px 14px;
                    border-radius: 30px;
                    font-size: 12px;
                    font-weight: 800;
                    display: inline-block;
                    letter-spacing: 0.5px;
                    box-shadow: 0 0 15px rgba(16, 185, 129, 0.1);
                }
                .status-active { background: rgba(16, 185, 129, 0.1); color: #059669; border: 1px solid rgba(16,185,129,0.2); }
                .status-inactive { background: rgba(239, 68, 68, 0.1); color: #dc2626; border: 1px solid rgba(239,68,68,0.2); }
                
                @media (max-width: 768px) {
                    .hero-section {
                        flex-direction: column;
                        text-align: center;
                        padding: 30px 20px;
                    }
                    .hero-badges {
                        justify-content: center;
                    }
                    .info-row {
                        flex-direction: column;
                        gap: 8px;
                        align-items: flex-start;
                    }
                    .info-value {
                        text-align: left;
                    }
                    .cards-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>

            {/* Hero Section */}
            <div className="hero-section">
                <div className="hero-logo-container">
                    {operatorDetails.companyLogo ? (
                        <img src={`${API_URL}/uploads/${operatorDetails.companyLogo}`} alt="Company Logo" />
                    ) : (
                        operatorDetails.companyName?.charAt(0) || 'O'
                    )}
                </div>
                <div className="hero-content">
                    <h1>{operatorDetails.companyName}</h1>
                    <div className="hero-badges">
                        {operatorDetails.industry && <span className="badge">{operatorDetails.industry}</span>}
                        {operatorDetails.status && (
                            <span className={`badge ${operatorDetails.status === 'Active' || operatorDetails.status === 'Approved' ? 'badge-active' : ''}`}>
                                {operatorDetails.status}
                            </span>
                        )}
                        {operatorDetails.website && (
                            <a href={operatorDetails.website.startsWith('http') ? operatorDetails.website : `https://${operatorDetails.website}`} target="_blank" rel="noreferrer" className="badge" style={{color: 'white', textDecoration: 'none'}}>
                                🌐 {operatorDetails.website}
                            </a>
                        )}
                    </div>
                </div>
            </div>

            <div className="cards-grid">
                
                {/* Active Subscription */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3 className="card-title">Active Subscription</h3>
                    </div>
                    {loading ? (
                        <div style={{color: '#6b7280', fontSize: 14}}>Loading subscription details...</div>
                    ) : subscription ? (
                        <div className="info-list">
                            <InfoRow label="Plan Name" value={subscription.planId?.name || 'Unknown Plan'} />
                            <div className="info-row">
                                <span className="info-label">Status</span>
                                <span className="info-value">
                                    <span className={`status-pill ${subscription.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                                        {subscription.status}
                                    </span>
                                </span>
                            </div>
                            <InfoRow label="Billing Amount" value={`${displayCurrency} ${Number(displayAmount).toLocaleString()}`} />
                            <InfoRow label="Start Date" value={formatDate(subscription.startDate)} />
                            <InfoRow label="Next Billing" value={formatDate(subscription.nextBillingDate)} />
                            {subscription.planId && (
                                <>
                                    <div style={{marginTop: 15, marginBottom: 10, borderTop: '1px dashed #cbd5e1', paddingTop: 15}}>
                                        <h4 style={{fontSize: 13, margin: '0 0 10px 0', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5}}>Plan Limits</h4>
                                        <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                                            <span className="badge" style={{background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12}}>
                                                Fleet: {subscription.planId.fleetLimit === -1 ? 'Unlimited' : subscription.planId.fleetLimit}
                                            </span>
                                            <span className="badge" style={{background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12}}>
                                                Drivers: {subscription.planId.driversLimit === -1 ? 'Unlimited' : subscription.planId.driversLimit}
                                            </span>
                                            <span className="badge" style={{background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12}}>
                                                Routes: {subscription.planId.routesLimit === -1 ? 'Unlimited' : subscription.planId.routesLimit}
                                            </span>
                                            <span className="badge" style={{background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12}}>
                                                Users: {subscription.planId.usersLimit === -1 ? 'Unlimited' : subscription.planId.usersLimit}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div style={{color: '#6b7280', fontStyle: 'italic', padding: '20px 0', textAlign: 'center', fontSize: 14}}>No active subscription found.</div>
                    )}
                </div>

                {/* Contact Information */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3 className="card-title">Contact Information</h3>
                    </div>
                    <div className="info-list">
                        <InfoRow label="Primary Email" value={operatorDetails.primaryEmail} />
                        <InfoRow label="Primary Phone" value={operatorDetails.primaryPhone} />
                        <InfoRow label="Contact Person" value={`${operatorDetails.contactPersonName || 'N/A'} ${operatorDetails.contactPersonPhone ? `(${operatorDetails.contactPersonPhone})` : ''}`} />
                        <InfoRow label="Emergency Contact" value={`${operatorDetails.emergencyContactName || 'N/A'} ${operatorDetails.emergencyPhone ? `(${operatorDetails.emergencyPhone})` : ''}`} />
                        <InfoRow label="Address" value={[operatorDetails.streetAddress, operatorDetails.city, operatorDetails.stateProvince, operatorDetails.postalCode, operatorDetails.country].filter(Boolean).join(', ')} />
                    </div>
                </div>

                {/* Operations & Logistics */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3 className="card-title">Operations & Logistics</h3>
                    </div>
                    <div className="info-list">
                        <InfoRow label="Timezone" value={operatorDetails.timezone} />
                        <InfoRow label="Fleet Size" value={operatorDetails.fleetSize ? `${operatorDetails.fleetSize} vehicles` : null} />
                        <InfoRow label="Drivers Count" value={operatorDetails.numberOfDrivers ? `${operatorDetails.numberOfDrivers} drivers` : null} />
                        <InfoRow label="Default Shifts" value={(operatorDetails.defaultShiftStart && operatorDetails.defaultShiftEnd) ? `${operatorDetails.defaultShiftStart} to ${operatorDetails.defaultShiftEnd}` : null} />
                        <InfoRow label="Primary Service Type" value={Array.isArray(operatorDetails.primaryServiceType) ? operatorDetails.primaryServiceType.join(', ') : operatorDetails.primaryServiceType} />
                    </div>
                </div>

                {/* Financials & Tax */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3 className="card-title">Financials & Tax</h3>
                    </div>
                    <div className="info-list">
                        <InfoRow label="Registration Number" value={operatorDetails.registrationNumber} />
                        <InfoRow label="Tax ID" value={operatorDetails.taxId} />
                        <InfoRow label="VAT Percentage" value={operatorDetails.vatPercentage ? `${operatorDetails.vatPercentage}%` : null} />
                        <InfoRow label="Fiscal Year Start" value={operatorDetails.fiscalYearStart} />
                        <InfoRow label="Default Currency" value={operatorDetails.defaultCurrency} />
                    </div>
                </div>

                {/* Bank Details */}
                <div className="profile-card" style={{gridColumn: '1 / -1'}}>
                    <div className="card-header">
                        <h3 className="card-title">Bank Details</h3>
                    </div>
                    <div className="info-list" style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px 60px'}}>
                        <InfoRow label="Bank Name" value={operatorDetails.bankName} />
                        <InfoRow label="Account Holder" value={operatorDetails.accountHolderName} />
                        <InfoRow label="Account Name" value={operatorDetails.accountName} />
                        <InfoRow label="Account Number" value={operatorDetails.accountNumber} />
                        <InfoRow label="IBAN" value={operatorDetails.iban} />
                        <InfoRow label="SWIFT Code" value={operatorDetails.swiftCode} />
                    </div>
                </div>

            </div>
        </div>
    );
}
